import React, { useState, useRef, useEffect } from 'react';
import ProjectSelector from './ProjectSelector';
import WorkspaceSelector from './WorkspaceSelector';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Skeleton from './Skeleton';
import {
  LayoutDashboard,
  Search,
  Bell,
  User as UserIcon,
  ChevronDown,
  LogOut,
  Briefcase,
  Columns,
  Globe,
  Settings as SettingsIcon,
  Plus,
  Database,
  RotateCcw,
  Zap,
  Camera,
  Crown,
  ClipboardCheck,
  Layers3,
  Beaker
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useProject } from '../context/ProjectContext';
import { motion, AnimatePresence, useScroll, useTransform, useMotionValue, useSpring } from 'framer-motion';
import AnimatedLogo from './AnimatedLogo';

const SidebarItem = ({ icon: Icon, label, subLabel, active, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all relative ${active ? 'text-primary' : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
      }`}
  >
    {active && (
      <motion.div
        layoutId="active-nav"
        className="absolute inset-0 bg-primary/10 border border-primary/20 rounded-xl shadow-[0_0_20px_rgba(37,99,235,0.1)]"
        initial={false}
        transition={{ duration: 0.4, ease: "circOut" }}
      />
    )}
    <Icon size={20} className="relative z-10" />
    <div className="flex flex-col items-start relative z-10 text-left">
      <span className="font-medium leading-tight">{label}</span>
      {subLabel && (
        <div className="mt-1 flex items-center gap-1.5">
          <div className="relative">
            <div className="absolute inset-0 bg-primary/20 blur-[4px] rounded-full animate-pulse" />
            <span className="relative text-[7px] font-black uppercase tracking-[0.15em] text-primary bg-primary/10 px-1.5 py-0.5 rounded-full border border-primary/20 whitespace-nowrap">
              {subLabel}
            </span>
          </div>
        </div>
      )}
    </div>
  </button>
);

const UserDropdown = ({ user, t, lang, setLang, onSignOut }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <motion.button
        whileHover={{ scale: 1.02, y: -1 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-3 pl-3 pr-2 py-1.5 rounded-2xl transition-all duration-300 outline-none border ${
          isOpen 
            ? 'bg-white/10 border-white/20 shadow-[0_0_20px_rgba(255,255,255,0.05)]' 
            : 'bg-white/5 border-white/5 hover:border-white/10 hover:bg-white/[0.08]'
        }`}
      >
        <div className="text-right hidden sm:block">
          <div className="text-xs font-black text-slate-100 tracking-tight">{user?.name}</div>
          <div className="text-[9px] text-slate-500 font-bold uppercase tracking-widest opacity-80">{user?.email?.split('@')[0]}</div>
        </div>
        <div className="relative">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary/30 to-secondary/30 border border-white/20 flex items-center justify-center overflow-hidden shadow-inner group-hover:border-primary/50 transition-colors">
            {user?.avatar ? (
              <img src={user.avatar} className="w-full h-full object-cover" alt="avatar" />
            ) : (
              <UserIcon size={18} className="text-white" />
            )}
          </div>
          <motion.div 
            animate={{ scale: isOpen ? 1 : 0 }}
            className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-[#0f172a] shadow-[0_0_10px_rgba(16,185,129,0.5)]" 
          />
        </div>
        <ChevronDown 
          size={14} 
          className={`text-slate-500 transition-transform duration-300 ${isOpen ? 'rotate-180 text-white' : ''}`} 
        />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 15, scale: 0.95, filter: 'blur(10px)' }}
            animate={{ opacity: 1, y: 8, scale: 1, filter: 'blur(0px)' }}
            exit={{ opacity: 0, y: 15, scale: 0.95, filter: 'blur(10px)' }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
            className="absolute right-0 w-72 bg-[#0f172a] backdrop-blur-2xl border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.5)] z-50 overflow-hidden rounded-2xl p-2 origin-top-right"
          >
            {/* Header Info */}
            <div className="p-4 mb-2 bg-white/[0.03] rounded-2xl border border-white/5">
                <div className="flex items-center gap-3 mb-3">
                   <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-secondary p-0.5 shadow-lg">
                      <div className="w-full h-full rounded-[10px] bg-[#0f172a] flex items-center justify-center overflow-hidden">
                         {user?.avatar ? <img src={user.avatar} className="w-full h-full object-cover" /> : <UserIcon size={24} className="text-primary" />}
                      </div>
                   </div>
                   <div className="flex-1 min-w-0">
                      <p className="text-sm font-black text-white truncate">{user?.name}</p>
                      <p className="text-[10px] text-slate-500 truncate font-semibold">{user?.email}</p>
                   </div>
                </div>
                <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-4" />
                
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 mb-3 px-1">{t('language')}</p>
                <div className="flex bg-slate-950/50 rounded-xl p-1 border border-white/5">
                  {['en', 'es'].map((l) => (
                    <button
                      key={l}
                      onClick={() => setLang(l)}
                      className={`flex-1 py-2 text-[10px] font-black rounded-lg transition-all relative ${lang === l ? 'text-white' : 'text-slate-500 hover:text-slate-300'}`}
                    >
                      {lang === l && (
                        <motion.div
                          layoutId="active-lang-nav"
                          className="absolute inset-0 bg-primary rounded-lg shadow-[0_0_15px_rgba(37,99,235,0.3)]"
                          transition={{ type: "spring", stiffness: 500, damping: 35 }}
                        />
                      )}
                      <span className="relative z-10">{l.toUpperCase()}</span>
                    </button>
                  ))}
                </div>
            </div>

            <div className="space-y-1 p-1">
              <button
                onClick={() => {
                  setIsOpen(false);
                  onSignOut();
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-slate-400 hover:text-rose-400 hover:bg-rose-400/10 transition-all text-xs font-black group"
              >
                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center group-hover:bg-rose-400/20 transition-colors">
                  <LogOut size={16} />
                </div>
                {t('signOut')}
              </button>
            </div>

            {/* Premium Glimmer Overlay */}
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-tr from-white/[0.02] via-transparent to-transparent" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const RestrictedView = ({ t }) => (
  <div className="flex flex-col items-center justify-center h-full space-y-8 animate-in fade-in zoom-in duration-500">
    <div className="w-24 h-24 rounded-3xl bg-rose-500/10 flex items-center justify-center text-rose-500 shadow-[0_0_50px_rgba(244,63,94,0.1)] border border-rose-500/20">
      <Briefcase size={48} strokeWidth={1.5} />
    </div>
    <div className="text-center max-w-sm">
      <h2 className="text-3xl font-display font-black text-slate-100 mb-3">{t('accessRestricted')}</h2>
      <p className="text-slate-500 text-sm leading-relaxed font-medium">
        {t('accessRestrictedDesc')}
      </p>
    </div>
  </div>
);

const formatStorage = (bytes) => {
  if (!bytes || bytes === 0) return '0B';
  if (bytes === Infinity) return 'Unlimited';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + sizes[i];
};

const UsageBar = ({ label, current, limit, unit = '', isBytes = false, icon: Icon, color = 'bg-primary', isFounder = false, t }) => {
  // Safe calculation for percentages
  const safeCurrent = current || 0;
  const safeLimit = limit === Infinity ? Infinity : (limit || 1);
  const percentage = limit === Infinity ? 0 : Math.min(100, (safeCurrent / safeLimit) * 100);
  
  const isHigh = !isFounder && percentage > 80;
  const isCritical = !isFounder && percentage >= 100;

  const formatValue = (val) => {
    if (val === Infinity) return '∞';
    if (val === null || val === undefined) return '0';
    if (isBytes) return formatStorage(val);
    return val.toLocaleString() + unit;
  };

  return (
    <div className="space-y-1.5 group">
      {/* Row 1: Label & Meta */}
      <div className="flex items-center justify-between px-0.5">
        <div className="flex items-center gap-1.5 text-slate-400 group-hover:text-slate-200 transition-colors">
          <Icon 
            size={12} 
            className={`${
              isFounder ? 'text-amber-400' :
              isCritical ? 'text-rose-500' : 
              isHigh ? 'text-amber-500' : 
              'text-primary'
            } opacity-70`} 
            strokeWidth={2.5} 
          />
          <span className={`text-[10px] font-black uppercase tracking-widest ${isFounder ? 'text-amber-500/90' : ''}`}>
            {label}
          </span>
        </div>
        <span className={`text-[10px] font-black tracking-tighter ${
          isFounder ? 'text-amber-400' :
          isCritical ? 'text-rose-500' : 
          isHigh ? 'text-amber-500' : 
          'text-slate-400'
        }`}>
          {isFounder ? '∞' : `${percentage.toFixed(0)}%`}
        </span>
      </div>

      {/* Row 2: Progress Bar */}
      <div className={`h-1.5 w-full bg-white/5 rounded-full overflow-hidden p-[1px] border ${isFounder ? 'border-amber-500/20' : 'border-white/5'} relative shadow-inner`}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: isFounder ? '100%' : `${percentage}%` }}
          transition={{ duration: 1.5, ease: "circOut" }}
          className={`h-full rounded-full relative z-10 ${
            isFounder ? 'bg-gradient-to-r from-amber-600 via-yellow-500 to-amber-600' :
            isCritical ? 'bg-rose-600' : 
            isHigh ? 'bg-amber-500' : 
            color
          }`}
          style={{ 
            boxShadow: isFounder ? '0 0 15px rgba(245,158,11,0.3)' :
                       isCritical ? '0 0 12px rgba(225,29,72,0.4)' : 
                       '0 0 10px rgba(37,99,235,0.2)' 
          }}
        >
          {isFounder && (
             <motion.div
               animate={{ x: ['-100%', '100%'] }}
               transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
               className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12"
             />
          )}
        </motion.div>
        {/* Subtle background glow */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.02] to-transparent pointer-events-none" />
      </div>

      {/* Row 3: Numeric Usage (Spaced Out) */}
      <div className="flex justify-end px-0.5">
        <span className="text-[9px] font-bold text-slate-500/80 tracking-wide font-mono">
          {isFounder ? (
            <span className="text-amber-500/60 uppercase italic tracking-tighter decoration-amber-500/30 underline underline-offset-4">{t('foundersUnlimited')}</span>
          ) : (
            <>
              <span className="text-slate-400">{formatValue(current)}</span>
              <span className="mx-1 text-slate-600">/</span>
              <span>{formatValue(limit)}</span>
            </>
          )}
        </span>
      </div>
    </div>
  );
};

const UsageTracker = ({ workspace, t }) => {
  if (!workspace) return null;

  const isFounder = workspace.isFoundersTeam;

  // Use limits from backend if available, fallback to safe defaults
  const limits = workspace.limits || {
    screenshots: 15,
    storage: 100 * 1024 * 1024
  };

  return (
    <div className="px-2 py-6 space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="flex items-center gap-2 px-1">
        <div className={`w-1.5 h-1.5 rounded-full ${isFounder ? 'bg-amber-500' : 'bg-primary'} shadow-[0_0_8px_rgba(${isFounder ? '245,158,11,0.8' : '37,99,235,0.8'})]`} />
        <h4 className={`text-[11px] font-black uppercase tracking-[0.2em] ${isFounder ? 'text-amber-500' : 'text-white'} opacity-80 italic`}>
          {isFounder ? t('foundersUsage') : t('usage')}
        </h4>
      </div>
      
      <div className="space-y-5">
        <UsageBar 
          icon={Camera}
          label={t('daily_screenshots')}
          current={workspace.usage?.dailyScreenshots || 0}
          limit={limits.screenshots}
          isFounder={isFounder}
          t={t}
        />
        <UsageBar 
          icon={Database}
          label={t('cloud_storage')}
          current={workspace.usage?.storageUsed || 0}
          limit={limits.storage}
          isBytes={true}
          color="bg-secondary"
          isFounder={isFounder}
          t={t}
        />
      </div>
    </div>
  );
};

const FoundersBadge = ({ t }) => {
  return (
    <motion.div
      initial={{ opacity: 0, x: -10, scale: 0.9 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      className="relative group/founder"
    >
      <div className="absolute inset-0 bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 rounded-xl blur-md opacity-20 group-hover:opacity-40 transition-opacity" />
      <div className="relative flex flex-col h-[48px] justify-center px-6 rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-400/10 via-yellow-500/10 to-amber-600/10 overflow-hidden">
        {/* Shimmer Effect */}
        <motion.div
          animate={{ x: ['100%', '-100%'] }}
          transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12"
        />
        
        <div className="flex items-center gap-3 relative z-10">
          <Crown size={24} className="text-amber-400 shrink-0" fill="currentColor" />
          <div className="flex flex-col">
            <span className="text-[8px] font-black uppercase tracking-[0.2em] text-amber-500/80 leading-none mb-1">Founders Team</span>
            <span className="text-[10px] font-black text-amber-200 leading-none italic">
              {t('unlimitedAccess')}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLanguage();
  const { canAccess, activeProject, activeWorkspace, activeProjectId, loading, projects, setActiveProjectId, userRole } = useProject();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const mainRef = useRef(null);

  const { scrollYProgress } = useScroll({
    container: mainRef
  });

  const backgroundY1 = useTransform(scrollYProgress, [0, 1], [0, -10]);
  const backgroundY2 = useTransform(scrollYProgress, [0, 1], [0, -20]);
  const backgroundScale = useTransform(scrollYProgress, [0, 1], [1, 1.005]);
  
  const backgroundY1Spring = useSpring(backgroundY1, { damping: 60, stiffness: 200 });
  const backgroundY2Spring = useSpring(backgroundY2, { damping: 60, stiffness: 200 });
  const backgroundScaleSpring = useSpring(backgroundScale, { damping: 60, stiffness: 200 });

  // Mouse Parallax
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const smoothMouseX = useSpring(mouseX, { damping: 50, stiffness: 400 });
  const smoothMouseY = useSpring(mouseY, { damping: 50, stiffness: 400 });
  const rotateX = useTransform(smoothMouseY, [-300, 300], [0.5, -0.5]);
  const rotateY = useTransform(smoothMouseX, [-300, 300], [-0.5, 0.5]);

  const handleMouseMove = (e) => {
    const rect = mainRef.current?.getBoundingClientRect();
    if (rect) {
      mouseX.set(e.clientX - rect.left - rect.width / 2);
      mouseY.set(e.clientY - rect.top - rect.height / 2);
    }
  };

  useEffect(() => {
    // New user onboarding: if no projects, always land on /projects
    // Simplified: Individual pages (Board, Backlog) handle their own access/redirects
    // DashboardLayout only shows the Sidebar/Header skeleton during load

    // If user is on Overview but doesn't have permissions or projects, redirect to board or projects
    // Actually, DashboardLayout handles isRestricted now, so we don't need a hard redirect if they have SOME projects.
  }, [loading, projects.length, location.pathname, navigate]);

  const isRestricted = !loading && projects.length > 0 && !canAccess(location.pathname);
  
  const menuItems = [
    { icon: LayoutDashboard, label: t('overview'), path: '/' },
    { icon: Briefcase, label: t('projects'), path: '/projects' },
    { icon: Database, label: t('backlogSprints'), path: '/backlog' },
    { icon: RotateCcw, label: t('history'), path: '/history' },
    { icon: ClipboardCheck, label: t('regressionTracking'), path: '/regression-tracking' },
    { icon: Columns, label: t('board'), path: '/board' },
    { icon: Layers3, label: t('components'), path: '/components' },
    { icon: Beaker, label: t('e2eTestFlows'), path: '/e2e' },
    { icon: SettingsIcon, label: t('configuration'), path: '/settings' },
  ];

  useEffect(() => {
    const activeItem = menuItems.find(item => item.path === location.pathname);
    if (activeItem) {
      document.title = `${activeItem.label} | ViewGate`;
    } else {
      // Fallback for subpaths or undefined routes
      if (location.pathname.includes('/project/')) {
        document.title = `${t('projectDetails')} | ViewGate`;
      } else {
        document.title = 'ViewGate';
      }
    }
  }, [location.pathname, t]);

  useEffect(() => {
    const container = mainRef.current;
    if (!container) return;

    container.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  // --- GLOBAL NAVIGATION SYNC (BroadcastChannel) ---
  useEffect(() => {
    try {
      const channel = new BroadcastChannel('viewgate_dashboard');
      channel.onmessage = (event) => {
        const { type, search, path } = event.data;
        if (type === 'OPEN_TICKET' && search) {
          // Navigate to the correct path with the search query
          const params = new URLSearchParams();
          if (activeProjectId) params.set('projectId', activeProjectId);
          params.set('search', search);
          
          const targetPath = path || '/board';
          navigate({ pathname: targetPath, search: params.toString() });
          window.focus();
        }
      };
      return () => channel.close();
    } catch (e) {
      /* BroadcastChannel not supported fallback */
    }
  }, [navigate, activeProjectId]);


  return (
    <div className="flex min-h-screen bg-background text-foreground font-sans selection:bg-primary/30">
      <div className="fixed top-0 left-1/4 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[120px] pointer-events-none" />

      <aside className="w-64 border-r border-white/5 glass-panel z-20 flex flex-col p-6 sticky top-0 h-screen">
        <Link to="/" className="flex items-center gap-3 mb-10 group/logo px-2">
          <AnimatedLogo />
        </Link>

        <nav className="flex-1 space-y-2">
          {menuItems.filter(item => canAccess(item.path)).map((item) => (
            <SidebarItem
              key={item.path}
              icon={item.icon}
              label={item.label}
              subLabel={item.subLabel}
              active={location.pathname === item.path}
              onClick={() => {
                const event = new CustomEvent('viewgate-navigate', { 
                  detail: { path: item.path },
                  cancelable: true 
                });
                window.dispatchEvent(event);
                if (!event.defaultPrevented) {
                  const searchParams = new URLSearchParams();
                  if (activeProjectId) searchParams.set('projectId', activeProjectId);
                  navigate({ pathname: item.path, search: searchParams.toString() });
                }
              }}
            />
          ))}
        </nav>

        <div className="pt-6 border-t border-white/5 mt-auto">
          <UsageTracker workspace={activeWorkspace} t={t} />

          <motion.button
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/subscription')}
            className={`w-full relative group/premium overflow-hidden rounded-[24px] p-[1.5px] ${
              activeWorkspace?.isFoundersTeam 
                ? 'bg-gradient-to-br from-amber-500 via-yellow-400 to-amber-600 shadow-[0_0_20px_rgba(245,158,11,0.25)]' 
                : 'bg-gradient-to-br from-primary/50 via-secondary/50 to-primary/50 shadow-[0_0_20px_rgba(37,99,235,0.1)]'
            } transition-all ${
              location.pathname === '/subscription' ? 'ring-2 ring-primary/30 ring-offset-4 ring-offset-background' : ''
            }`}
          >
            <div className={`absolute inset-0 bg-gradient-to-r ${activeWorkspace?.isFoundersTeam ? 'from-amber-400/20 via-transparent to-amber-400/20' : 'from-primary/20 via-transparent to-primary/20'} translate-x-[-100%] group-hover/premium:translate-x-[100%] transition-transform duration-1000`} />
            <div className="bg-[#0f172a] rounded-[22.5px] px-4 py-3.5 flex items-center justify-between relative z-10 border border-white/5">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors duration-300 ${
                    activeWorkspace?.isFoundersTeam
                      ? 'bg-amber-500/10 text-amber-500 group-hover/premium:bg-amber-500 group-hover/premium:text-slate-900'
                      : location.pathname === '/subscription' ? 'bg-primary text-slate-900' : 'bg-primary/10 text-primary group-hover/premium:bg-primary group-hover/premium:text-slate-900'
                  }`}>
                    {activeWorkspace?.isFoundersTeam ? <Crown size={16} fill="currentColor" /> : <Zap size={16} fill="currentColor" />}
                  </div>
                  <div className="text-left">
                    <div className="text-[10px] font-black text-white uppercase tracking-widest leading-none mb-1">
                      {activeWorkspace?.isFoundersTeam ? 'Founders' : 'Platinum'}
                    </div>
                    <div className={`text-[8px] font-bold ${activeWorkspace?.isFoundersTeam ? 'text-amber-500/80' : 'text-slate-500'} uppercase tracking-tighter leading-none group-hover/premium:text-slate-400 transition-colors`}>
                      {activeWorkspace?.isFoundersTeam ? t('unlimitedAccess') : t(activeProject?.subscription?.status || 'trial')}
                    </div>
                  </div>
                </div>
               <div className={`w-2 h-2 rounded-full animate-pulse shadow-[0_0_10px_currentColor] ${
                 activeWorkspace?.isFoundersTeam ? 'bg-amber-500 text-amber-500' :
                 activeProject?.subscription?.status === 'active' ? 'bg-emerald-500 text-emerald-500' : 
                 activeProject?.subscription?.status === 'trial' ? 'bg-blue-500 text-blue-500' : 'bg-red-500 text-red-500'
               }`} />
            </div>
          </motion.button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col relative z-10 overflow-hidden">
        <header className="h-16 border-b border-white/5 glass-panel px-8 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-4 flex-1">
            <WorkspaceSelector />
            <ProjectSelector />
            {loading ? (
              <div className="flex flex-col h-[48px] justify-center px-6 rounded-xl border border-white/5 bg-white/5 min-w-[100px]">
                <Skeleton width="30px" height="6px" className="mb-1.5 opacity-50" />
                <Skeleton width="50px" height="10px" />
              </div>
            ) : userRole && (
              <div className={`flex flex-col h-[48px] justify-center px-6 rounded-xl border ${userRole === 'pm'
                  ? 'bg-primary/10 border-primary/20 text-primary'
                  : 'bg-amber-500/10 border-amber-500/20 text-amber-500'
                }`}>
                <span className="text-[8px] font-black uppercase tracking-[0.2em] opacity-50 leading-none mb-1">{t('role')}</span>
                <span className="text-[10px] font-bold leading-none">
                  {t('role_' + userRole)}
                </span>
              </div>
            )}
            
            {activeWorkspace?.isFoundersTeam && (
              <FoundersBadge t={t} />
            )}
          </div>

          <div className="flex items-center gap-4">
            <div className="group relative">
              <div className="absolute right-0 top-full mt-4 w-64 bg-[#0f172a] border border-white/10 p-8 opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100 transition-all pointer-events-none rounded-2xl shadow-2xl z-50">
                <p className="text-xs text-center font-bold text-slate-400">{t('noNotifications')}</p>
              </div>
            </div>
            <div className="h-8 w-px bg-white/10 mx-2" />

            <UserDropdown
              user={user}
              onSignOut={() => setShowLogoutConfirm(true)}
              t={t}
              lang={lang}
              setLang={setLang}
            />
          </div>
        </header>

        <div className="flex-1 relative overflow-hidden">
          {/* Parallax Background Layer - Isolated from scroll */}
          {!['/regression-tracking'].includes(location.pathname) && (
            <div className="absolute inset-0 -z-10 pointer-events-none overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-40" />
            
            {/* Layer 1: Far, Small Dots */}
            <motion.div 
              className="absolute inset-[-15%] opacity-[0.15]" 
              style={{ 
                backgroundImage: `radial-gradient(rgba(255, 255, 255, 0.3) 0.5px, transparent 0)`,
                backgroundSize: `16px 16px`,
                maskImage: `radial-gradient(circle at center, black 10%, transparent 80%)`,
                WebkitMaskImage: `radial-gradient(circle at center, black 10%, transparent 80%)`,
                y: backgroundY1Spring,
                scale: backgroundScaleSpring,
                rotateX,
                rotateY,
                transformPerspective: 1500,
              }} 
            />

            {/* Layer 2: Mid, Regular Dots */}
            <motion.div 
              className="absolute inset-[-10%] opacity-[0.22]" 
              style={{ 
                backgroundImage: `radial-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 0)`,
                backgroundSize: `32px 32px`,
                maskImage: `linear-gradient(to bottom, transparent, black 20%, black 80%, transparent), radial-gradient(circle at center, black 30%, transparent 90%)`,
                WebkitMaskImage: `linear-gradient(to bottom, transparent, black 20%, black 80%, transparent), radial-gradient(circle at center, black 30%, transparent 90%)`,
                WebkitMaskComposite: 'destination-in',
                maskComposite: 'intersect',
                y: backgroundY2Spring,
                rotateX,
                rotateY,
                transformPerspective: 1200,
              }} 
            />
          </div>
        )}

        <main 
            ref={mainRef} 
            onMouseMove={handleMouseMove}
            className={`absolute inset-0 scroll-smooth flex flex-col ${
              ['/subscription', '/regression-tracking', '/backlog'].includes(location.pathname) ? 'p-0 overflow-hidden' : 'p-10 overflow-y-auto'
            }`}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: 10, filter: 'blur(10px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                exit={{ opacity: 0, y: -10, filter: 'blur(10px)' }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                className="h-full"
              >
                {isRestricted ? <RestrictedView t={t} /> : children}
              </motion.div>
            </AnimatePresence>
          </main>
        </div>
      </div>

      <AnimatePresence>
        {showLogoutConfirm && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowLogoutConfirm(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-sm bg-[#0f172a] border border-white/10 rounded-[2.5rem] p-8 shadow-2xl overflow-hidden"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-2xl bg-red-500/10 flex items-center justify-center text-red-500 mb-6 border border-red-500/20">
                  <LogOut size={32} />
                </div>
                
                <h3 className="text-xl font-display font-black text-white mb-2 italic">
                  {t('confirmSignOut')}
                </h3>
                <p className="text-slate-400 text-sm mb-8 leading-relaxed">
                  {t('confirmSignOutMsg')}
                </p>

                <div className="flex gap-3 w-full">
                  <button
                    onClick={() => setShowLogoutConfirm(false)}
                    className="flex-1 px-4 py-3 rounded-2xl bg-white/5 hover:bg-white/10 text-slate-300 text-sm font-bold transition-all border border-white/5"
                  >
                    {t('cancel')}
                  </button>
                  <button
                    onClick={logout}
                    className="flex-1 px-4 py-3 rounded-2xl bg-red-500 hover:bg-red-600 text-white text-sm font-bold transition-all shadow-[0_0_20px_rgba(239,68,68,0.2)]"
                  >
                    {t('signOut')}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DashboardLayout;
